import type {
  AIRun,
  AiCostRow,
  AiCostGroupBy,
  AdminAuth,
  AdminSettings,
  AuditEntry,
  BillingMetrics,
  ChatMessage,
  CustomerContext,
  EnvironmentInfo,
  HealthMetrics,
  HealthTimelinePoint,
  Integration,
  PlatformAlert,
  PlatformMetrics,
  QueueStat,
  SendMessageInput,
  ServiceHealth,
  Tenant,
  Ticket
} from "./types";

declare const process: { env: { API_URL?: string } };

// Webpack's EnvironmentPlugin replaces `process.env.API_URL` with the literal
// build-time string. The previous guard (`typeof process !== "undefined"` &&
// `process.env`) short-circuited to "" in the browser because `process` is not
// a runtime global — even though webpack had already substituted the value.
// Reading `process.env.API_URL` directly lets webpack's static replacement
// stand on its own.
export const API_URL: string = process.env.API_URL || "";

export const isApiConfigured = (): boolean => API_URL.length > 0;

class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

/**
 * Pull a human-readable reason out of an upstream error body so we can
 * surface things like "two-person rule: approver cannot be the requester"
 * instead of a bare HTTP status. Prefers `message`, falls back to `error`,
 * and returns null when the body has neither (caller should use a generic
 * status-based string then).
 */
export function extractErrorMessage(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const obj = body as Record<string, unknown>;
  if (typeof obj.message === "string" && obj.message.trim()) return obj.message.trim();
  if (typeof obj.error === "string" && obj.error.trim()) return obj.error.trim();
  return null;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  if (!isApiConfigured()) {
    throw new ApiError(
      "API_URL is not configured. Set the API_URL environment variable to connect this admin portal to a backend.",
      0
    );
  }
  const res = await fetch(`${API_URL}${path}`, {
    method,
    credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined
  });
  if (!res.ok) {
    // Prefer the upstream JSON body's `message` so policy/auth/validation
    // failures surface their reason ("two-person rule: approver cannot be
    // the requester", "MFA required", etc.) instead of a bare status code.
    let detail: string | null = null;
    try {
      detail = extractErrorMessage(await res.clone().json());
    } catch {
      // Non-JSON or empty body — fall through to status-only message.
    }
    const fallback = `${method} ${path} failed: ${res.status}`;
    throw new ApiError(detail ?? fallback, res.status);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const get = <T>(path: string) => request<T>("GET", path);
const post = <T>(path: string, body?: unknown) => request<T>("POST", path, body);

export const listTenants = () => get<Tenant[]>("/tenants");
export const listTickets = (filter?: { mine?: boolean; unassigned?: boolean }) => {
  const qs = filter
    ? Object.entries(filter)
        .filter(([, v]) => v === true)
        .map(([k]) => `${k}=true`)
        .join("&")
    : "";
  return get<Ticket[]>(qs ? `/support/tickets?${qs}` : "/support/tickets");
};

export const listTicketMessages = (ticketId: string) =>
  get<ChatMessage[]>(`/support/tickets/${encodeURIComponent(ticketId)}/messages`);
export const getCustomerContext = (ticketId: string) =>
  get<CustomerContext>(`/support/tickets/${encodeURIComponent(ticketId)}/context`);
export const sendTicketMessage = ({ ticketId, text }: SendMessageInput) =>
  post<ChatMessage>(`/support/tickets/${encodeURIComponent(ticketId)}/messages`, { text });

export const markTicketRead = (ticketId: string) =>
  post<{ id: string; unreadCount: number }>(
    `/support/tickets/${encodeURIComponent(ticketId)}/read`,
  );

export const claimTicket = (ticketId: string) =>
  post<Ticket>(`/support/tickets/${encodeURIComponent(ticketId)}/claim`);

export const reassignTicket = (
  ticketId: string,
  newAgentId: string,
  newAgentFirstName: string,
) =>
  post<Ticket>(`/support/tickets/${encodeURIComponent(ticketId)}/reassign`, {
    newAgentId,
    newAgentFirstName,
  });

export const closeTicket = (ticketId: string) =>
  post<Ticket>(`/support/tickets/${encodeURIComponent(ticketId)}/close`);

export const ESCALATION_QUEUES = [
  "legal",
  "billing",
  "security",
  "engineering",
  "sales",
  "integration",
] as const;
export type EscalationQueue = (typeof ESCALATION_QUEUES)[number];

export interface EscalationEntry {
  id: string;
  fromStatus: string;
  toStatus: string;
  queue: string | null;
  teamId: string | null;
  escalatedBy: string | null;
  escalatedByEmail: string | null;
  reason: string | null;
  escalatedAt: string;
}

export interface EscalationResult {
  ticket: Ticket;
  history: EscalationEntry;
}

export const escalateTicket = (
  ticketId: string,
  args: { queue: EscalationQueue; teamId?: string; reason?: string },
) =>
  post<EscalationResult>(
    `/support/tickets/${encodeURIComponent(ticketId)}/escalate`,
    args,
  );

export const addInternalNote = (ticketId: string, body: string) =>
  post<ChatMessage>(
    `/support/tickets/${encodeURIComponent(ticketId)}/notes`,
    { body },
  );

export const listEscalations = (ticketId: string) =>
  get<EscalationEntry[]>(
    `/support/tickets/${encodeURIComponent(ticketId)}/escalations`,
  );

export const assignTicketOwner = (ticketId: string, ownerId: string) =>
  post<Ticket>(`/support/tickets/${encodeURIComponent(ticketId)}/assign`, { ownerId });

export interface SupportMacro {
  id: string;
  name: string;
  body: string;
  updatedAt: string;
}

export const listSupportMacros = () => get<SupportMacro[]>("/support/macros");
export const createSupportMacro = (input: { name: string; body: string }) =>
  post<SupportMacro>("/support/macros", input);
export const updateSupportMacro = (id: string, input: { name?: string; body?: string }) =>
  patch<SupportMacro>(`/support/macros/${encodeURIComponent(id)}`, input);
export const deleteSupportMacro = (id: string) =>
  del<void>(`/support/macros/${encodeURIComponent(id)}`);

export interface OrgDepartmentRef {
  id: string;
  key: string;
  name: string;
}

export interface OrgManagerRef {
  id: string;
  fullName: string;
}

export interface AdminTeamMember {
  id: string;
  email: string;
  fullName: string;
  active: boolean;
  // Backend (admin-bff users.service) returns this as `roleKeys`. The earlier
  // `roles` field never matched the wire shape, so `u.roles.join(...)` threw
  // "Cannot read properties of undefined" and unmounted the whole admin
  // portal on /#users-teams.
  roleKeys: string[];
  // Org hierarchy (migration 0009). Null until assigned.
  title?: string | null;
  department?: OrgDepartmentRef | null;
  manager?: OrgManagerRef | null;
}

export const listAdminUsers = () => get<AdminTeamMember[]>("/users");

export interface AdminRole {
  key: string;
  name: string;
  description: string;
}

export const listAdminRoles = () => get<AdminRole[]>("/users/roles");

export const inviteAdminUser = (input: { email: string; fullName: string; roleKeys: string[] }) =>
  post<{ id: string }>("/users", input);

const patch = <T>(path: string, body?: unknown) => request<T>("PATCH", path, body);
const del = <T>(path: string) => request<T>("DELETE", path);

export const updateAdminUser = (
  id: string,
  patchInput: { active?: boolean; roleKeys?: string[] },
) => patch<{ ok: true }>(`/users/${encodeURIComponent(id)}`, patchInput);

export const deactivateAdminUser = (id: string) =>
  del<{ ok: true }>(`/users/${encodeURIComponent(id)}`);

// ── Org hierarchy (migration 0009) ───────────────────────────────────
export interface TeamMember {
  id: string;
  fullName: string;
  email: string;
  title: string | null;
  active: boolean;
  lastLoginAt: string | null;
  department: { key: string; name: string } | null;
  roleKeys: string[];
}

export const getMyTeam = () => get<TeamMember[]>("/users/my-team");

export interface Department {
  id: string;
  key: string;
  name: string;
  description: string | null;
}

export const listDepartments = () => get<Department[]>("/users/departments");

export const assignUserOrg = (
  id: string,
  body: { departmentId?: string | null; managerId?: string | null; title?: string | null },
) => patch<{ ok: true }>(`/users/${encodeURIComponent(id)}/org`, body);

// ----- Pillar 2: team performance & manager rollups -----

export interface EmployeeKpis {
  userId: string;
  actionsTotal: number;
  actionsSucceeded: number;
  actionsFailed: number;
  successRate: number | null;
  lastActionAt: string | null;
  approvalsRequested: number;
  approvalsActioned: number;
  approvalsPending: number;
  ticketsResolved: number;
  avgResolutionMinutes: number | null;
  ticketsOpen: number;
  liveChatActive: number;
  opsAvailable: boolean;
}
export interface TeamMemberPerf {
  id: string;
  fullName: string;
  email: string;
  title: string | null;
  department: string | null;
  kpis: EmployeeKpis;
}
export interface MyTeamPerformance {
  window: string;
  generatedAt: string;
  team: TeamMemberPerf[];
  summary: {
    reports: number;
    actionsTotal: number;
    approvalsActioned: number;
    ticketsOpen: number;
    ticketsResolved: number;
  };
}
export interface EmployeeActivityEvent {
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  result: string;
  occurred_at: string;
}
export interface EmployeePerformanceDetail {
  user: { id: string; fullName: string; email: string; title: string | null; department: string | null };
  window: string;
  kpis: EmployeeKpis;
  timeline: EmployeeActivityEvent[];
}
export interface CompanyRollup {
  window: string;
  generatedAt: string;
  headcountByDepartment: Array<{ department: string; active: number; total: number }>;
  totalActionsInWindow: number;
  pendingApprovals: number;
}

export const getMyTeamPerformance = (window = "7d") =>
  get<MyTeamPerformance>(`/users/my-team/performance?window=${encodeURIComponent(window)}`);
export const getEmployeePerformance = (id: string, window = "7d") =>
  get<EmployeePerformanceDetail>(
    `/users/${encodeURIComponent(id)}/performance?window=${encodeURIComponent(window)}`,
  );
export const getCompanyRollup = (window = "7d") =>
  get<CompanyRollup>(`/exec/company-rollup?window=${encodeURIComponent(window)}`);

export const listIntegrations = () => get<Integration[]>("/integrations");
export const listQueues = () => get<QueueStat[]>("/integrations/queues");

// Provider Operations Console — Wave A endpoints.
export interface ProviderHealth {
  providerId: string;
  name: string;
  category: string;
  status: "healthy" | "needs_attention" | "down";
  lastSyncAt: string | null;
  failureCount24h: number;
  connectedTenants: number | null;
  paused: boolean;
  pausedAt: string | null;
  pausedBy: string | null;
  pausedReason: string | null;
}

export interface ProviderPauseState {
  providerId: string;
  paused: boolean;
  pausedAt: string | null;
  pausedBy: string | null;
  pausedReason: string | null;
  updatedAt: string;
}

export interface ProviderTestResult {
  providerId: string;
  status: "ok" | "warn" | "error";
  message: string;
  checkedAt: string;
}

// C4.1 — cross-tenant provider health rollup from the CRM Integration Hub.
export interface ProviderHealthRollup {
  provider: string;
  connectedTenants: number;
  totalIntegrations: number;
  activeIntegrations: number;
  errorIntegrations: number;
  events24h: number;
  failures24h: number;
  rejected24h: number;
  errorRate24h: number;
  webhooks24h: number;
  webhookFailures24h: number;
  dlqDepth: number;
  lastSyncAt: string | null;
  runs24h: number;
  syncUptime24h: number | null;
}

export interface ProviderHealthRollupResponse {
  providers: ProviderHealthRollup[];
  total: number;
  generatedAt: string;
}

export const getProviderHealthRollup = () =>
  get<ProviderHealthRollupResponse>("/integrations/provider-health");

export interface DlqReplayResult {
  found: number;
  replayed: number;
  skipped: number;
  failed: number;
  capped: boolean;
}

// C4 — bulk DLQ replay behind the "Retry Failed Jobs" control.
export const replayDlq = () => post<DlqReplayResult>("/integrations/dlq/replay");

export const getProviderHealth = (providerId: string) =>
  get<ProviderHealth>(`/integrations/${encodeURIComponent(providerId)}/health`);

export const setProviderPaused = (
  providerId: string,
  body: { paused: boolean; reason?: string | null },
) =>
  post<ProviderPauseState>(
    `/integrations/${encodeURIComponent(providerId)}/pause`,
    body,
  );

export const testProviderConnection = (providerId: string) =>
  post<ProviderTestResult>(
    `/integrations/${encodeURIComponent(providerId)}/test-connection`,
  );

export interface ProviderConfig {
  providerId: string;
  configured: boolean;
  clientId: string | null;
  clientIdLabel: string;
  secretLabel: string;
  hasSecret: boolean;
  lastRotatedAt: string | null;
  redirectUris: string[];
  webhookUrl: string | null;
  notConfiguredReason: string | null;
}

export interface RotateSecretResult {
  providerId: string;
  rotatedAt: string;
  versionId: string;
}

export const getProviderConfig = (providerId: string) =>
  get<ProviderConfig>(`/integrations/${encodeURIComponent(providerId)}/config`);

export const rotateProviderSecret = (providerId: string, newSecret: string) =>
  post<RotateSecretResult>(
    `/integrations/${encodeURIComponent(providerId)}/rotate-secret`,
    { newSecret },
  );

export const getPlatformMetrics = () => get<PlatformMetrics>("/metrics/platform");
export const getBillingMetrics = () => get<BillingMetrics>("/metrics/billing");
export const getHealthMetrics = () => get<HealthMetrics>("/metrics/health");
export const getHealthServices = () =>
  get<ServiceHealth[]>("/metrics/health/services");
export const getHealthTimeline = () =>
  get<HealthTimelinePoint[]>("/metrics/health/timeline");
export const listPlatformAlerts = () => get<PlatformAlert[]>("/metrics/alerts");

export const listAIRuns = () => get<AIRun[]>("/ai/runs");

// C2.3 cross-tenant AI cost dashboard (admin-bff ai module → agent admin endpoints).
export const getAiCosts = (q?: { from?: string; to?: string; group_by?: AiCostGroupBy }) => {
  const params = new URLSearchParams();
  if (q?.from) params.set("from", q.from);
  if (q?.to) params.set("to", q.to);
  if (q?.group_by) params.set("group_by", q.group_by);
  const qs = params.toString();
  return get<{ costs: AiCostRow[] }>(`/ai/costs${qs ? `?${qs}` : ""}`);
};

export const setTenantAiBudget = (
  tenantId: string,
  body: { monthly_cost_cap_micro_usd_override?: number | null; monthly_overage_cap_micro_usd?: number | null },
) => post<{ budget: unknown }>(`/ai/tenants/${encodeURIComponent(tenantId)}/budget`, body);

export interface AuditFilter {
  actor?: string;
  action?: string;
  tenantId?: string;
  from?: string;
  to?: string;
  limit?: number;
}

function auditQuery(filter: AuditFilter): string {
  const parts: string[] = [];
  if (filter.actor) parts.push(`actor=${encodeURIComponent(filter.actor)}`);
  if (filter.action) parts.push(`action=${encodeURIComponent(filter.action)}`);
  if (filter.tenantId) parts.push(`tenantId=${encodeURIComponent(filter.tenantId)}`);
  if (filter.from) parts.push(`from=${encodeURIComponent(filter.from)}`);
  if (filter.to) parts.push(`to=${encodeURIComponent(filter.to)}`);
  if (filter.limit != null) parts.push(`limit=${filter.limit}`);
  return parts.length > 0 ? "?" + parts.join("&") : "";
}

export const listAuditEntries = (filter: AuditFilter = {}) =>
  get<AuditEntry[]>(`/security/audit${auditQuery(filter)}`);

// auditExportUrl returns the absolute URL to the CSV export so the
// browser can issue a credentialed GET (anchor click) and trigger a
// native download. Gated server-side by security:audit_export — the
// download will 403 if the actor lacks the perm.
export const auditExportUrl = (filter: AuditFilter = {}): string =>
  `${API_URL}/security/audit/export.csv${auditQuery(filter)}`;

export const listSecurityErrors = (filter: AuditFilter = {}) =>
  get<AuditEntry[]>(`/security/errors${auditQuery(filter)}`);
export const listSecurityLogs = (filter: AuditFilter = {}) =>
  get<AuditEntry[]>(`/security/logs${auditQuery(filter)}`);

// ----- A4.2 Policy Simulator -----
// All routes proxy admin-bff /policy/* → ivorycom-policy. Reads are gated
// server-side by policy:simulate, mutations by policy:write.

export interface PolicyFieldRule {
  field: string;
  read: boolean;
  write: boolean;
  mask: string | null;
}

export interface PolicyObjectRule {
  object: string;
  create: boolean;
  read: boolean;
  update: boolean;
  delete: boolean;
  fields: PolicyFieldRule[];
}

export interface PolicyDefinition {
  id: string;
  tenant: string | null;
  role: string;
  description?: string | null;
  objects: PolicyObjectRule[];
  createdAt?: string;
  updatedAt?: string;
}

export interface PolicyDefinitionInput {
  tenant?: string | null;
  role: string;
  description?: string | null;
  objects: PolicyObjectRule[];
}

export type PolicyAction = "create" | "read" | "update" | "delete";

export interface SimulateRequest {
  tenant: string;
  role: string;
  object: string;
  action: PolicyAction;
  field?: string;
}

export interface SimulateResult {
  allow: boolean;
  visible_fields: string[];
  masked_fields: Array<{ field: string; strategy: string }>;
  reason: string;
}

export interface PolicyHistoryEntry {
  id: string;
  actor: string;
  action: string;
  role: string | null;
  tenant: string | null;
  summary: string;
  occurredAt: string;
}

export const listPolicyDefinitions = (tenant?: string) =>
  get<PolicyDefinition[]>(
    `/policy/definitions${tenant ? `?tenant=${encodeURIComponent(tenant)}` : ""}`,
  );

export const createPolicyDefinition = (input: PolicyDefinitionInput) =>
  post<PolicyDefinition>("/policy/definitions", input);

export const updatePolicyDefinition = (id: string, input: PolicyDefinitionInput) =>
  request<PolicyDefinition>("PUT", `/policy/definitions/${encodeURIComponent(id)}`, input);

export const deletePolicyDefinition = (id: string) =>
  del<void>(`/policy/definitions/${encodeURIComponent(id)}`);

export const simulatePolicy = (req: SimulateRequest) =>
  post<SimulateResult>("/policy/simulate", req);

export const listPolicyHistory = (days = 90) =>
  get<PolicyHistoryEntry[]>(`/policy/history?days=${days}`);

export const getAdminSettings = () => get<AdminSettings>("/system/settings");
export const updateAdminSettings = (
  patchInput: Partial<{ supportSLA: string; chatRouting: string; dataRetention: string; featureFlags: string }>,
) => patch<AdminSettings>("/system/settings", patchInput);

export interface FeatureFlagRow {
  id: string;
  flagKey: string;
  tenantId: string | null;
  enabled: boolean;
  metadata?: Record<string, unknown> | null;
  updatedBy?: string | null;
  updatedAt: string;
}

export const listFeatureFlags = () => get<FeatureFlagRow[]>("/feature-flags");
export const upsertFeatureFlag = (input: {
  flagKey: string;
  tenantId?: string;
  enabled: boolean;
}) => request<FeatureFlagRow>("PUT", "/feature-flags", input);

export interface AnnouncementRow {
  id: string;
  title: string;
  body: string;
  audience: string;
  publishedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
}

export const listAnnouncements = () => get<AnnouncementRow[]>("/announcements");
export const createAnnouncement = (input: {
  title: string;
  body: string;
  audience: string;
  expiresAt?: string;
}) => post<AnnouncementRow>("/announcements", input);

// ── Approval policies ────────────────────────────────────────────────
export interface PolicyTier {
  max_cents?: number | null;
  role: string;
}

export interface ApprovalPolicies {
  refund?: PolicyTier[];
  credit?: PolicyTier[];
  tenant_terminate?: PolicyTier[];
  tenant_suspend?: PolicyTier[];
  mass_action_min_tenants?: number;
  impersonation_max_minutes?: number;
}

export const getApprovalPolicies = () => get<ApprovalPolicies>("/settings/approval-policies");
export const updateApprovalPolicies = (policies: ApprovalPolicies) =>
  request<ApprovalPolicies>("PUT", "/settings/approval-policies", policies);
export const getEnvironmentInfo = () => get<EnvironmentInfo>("/system/environment");
export const getAdminAuth = () => get<AdminAuth>("/system/admin-auth");

export interface CurrentUser {
  id: string;
  email: string;
  fullName: string;
  roles: string[];
  permissions: string[];
  // Org hierarchy (migration 0009). Optional — older tokens omit them.
  title?: string | null;
  department?: OrgDepartmentRef | null;
  manager?: OrgManagerRef | null;
}

export const getCurrentUser = () => get<CurrentUser>("/auth/me");
export const logout = () => post<void>("/auth/logout");

// ── MFA ──────────────────────────────────────────────────────────────
export interface MfaStatus {
  enabled: boolean;
  enrolledAt: string | null;
  recoveryCodesRemaining: number;
}

export interface MfaEnrolment {
  secret: string;
  otpauthUrl: string;
  qrCodeDataUrl: string;
  recoveryCodes: string[];
}

export const getMfaStatus = () => get<MfaStatus>("/auth/mfa/status");
export const enrollMfa = () => post<MfaEnrolment>("/auth/mfa/enroll");
export const confirmMfa = (code: string) => post<MfaStatus>("/auth/mfa/confirm", { code });
export const disableMfa = (code: string) => post<MfaStatus>("/auth/mfa/disable", { code });
// challengeMfa runs against the public /auth/mfa/challenge endpoint
// during the post-SSO MFA prompt — succeeds → admin_session is set
// and the SPA can re-fetch /auth/me.
export const challengeMfa = (code: string) => post<{ ok: true }>("/auth/mfa/challenge", { code });

// ── Approvals ────────────────────────────────────────────────────────
export type ApprovalStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "expired"
  | "executed";

export interface ApprovalRequest {
  id: string;
  action: string;
  payload: Record<string, unknown>;
  status: ApprovalStatus;
  requestedBy: string;
  requestedByEmail?: string;
  approvedBy: string | null;
  approvedByEmail?: string;
  rejectedReason: string | null;
  thresholdPolicy: string | null;
  expiresAt: string;
  requestedAt: string;
  resolvedAt: string | null;
}

export const listApprovals = (status?: ApprovalStatus) =>
  get<ApprovalRequest[]>(
    status ? `/approvals?status=${encodeURIComponent(status)}` : "/approvals",
  );

export const getPendingApprovalsCount = () =>
  get<{ count: number }>("/approvals/pending-count");

export const approveApproval = (id: string) =>
  post<ApprovalRequest>(`/approvals/${encodeURIComponent(id)}/approve`);

export const rejectApproval = (id: string, reason: string) =>
  post<ApprovalRequest>(`/approvals/${encodeURIComponent(id)}/reject`, { reason });

// Wired suspend — returns either {id, status} (auto-approved) or
// {status: "pending_approval", approvalId, ...} (pending).
export const suspendTenant = (tenantId: string, reason: string) =>
  post<unknown>(`/tenants/${encodeURIComponent(tenantId)}/suspend`, { reason });

// ── Impersonation ────────────────────────────────────────────────────
// Mints an auth-service tenant-scoped token the operator opens in a new
// tab to act as a customer user. ImpersonationService persists every
// mint in impersonation_tokens for audit + revocation.
export interface ImpersonationStartResult {
  id: string;
  token: string;
  consumeUrl: string | null;
  expiresAt: string;
  noiseTag: string;
}

export const startImpersonation = (input: {
  tenantId: string;
  targetUserId: string;
  reason: string;
  durationSeconds: number;
}) => post<ImpersonationStartResult>("/impersonation", input);

export const revokeImpersonation = (id: string) =>
  request<{ ok: true }>("DELETE", `/impersonation/${encodeURIComponent(id)}`);

export const resumeTenant = (tenantId: string) =>
  post<unknown>(`/tenants/${encodeURIComponent(tenantId)}/resume`);

// terminateTenant always routes through approvals (CFO+OWNER two-person
// rule per seeded policy). Response envelope mirrors suspend.
export const terminateTenant = (
  tenantId: string,
  reason: string,
  gracePeriodDays: number,
) =>
  post<unknown>(`/tenants/${encodeURIComponent(tenantId)}/terminate`, {
    reason,
    gracePeriodDays,
  });

// refund/credit auto-execute when the actor's tier covers the requested
// amount; otherwise the 202 envelope routes to the Approvals page.
// Auto-executed shape mirrors AdminBillingActionResult on the BFF.
export const refundTenant = (tenantId: string, amountCents: number, reason: string) =>
  post<unknown>(`/tenants/${encodeURIComponent(tenantId)}/refund`, { amountCents, reason });

export const creditTenant = (tenantId: string, amountCents: number, reason: string) =>
  post<unknown>(`/tenants/${encodeURIComponent(tenantId)}/credit`, { amountCents, reason });

// Bulk suspend N tenants in one shot. Routes through mass_action policy
// — auto-executes under the threshold, otherwise 202 with approval id.
export const bulkSuspendTenants = (tenantIds: string[], reason: string) =>
  post<unknown>("/tenants/bulk/suspend", { tenantIds, reason });

// ── Plan override (Slice D) ──────────────────────────────────────────
// Manual admin lever to override a tenant's effective plan for feature
// gating, without touching billing/subscriptions. Reason + actor are
// persisted on the tenants row by auth-service for the audit trail.
// expiresAt is optional ISO string; null/omitted means the override
// never auto-expires (manual revocation only).
export interface TenantPlanOverrideInput {
  plan: 'starter' | 'growth' | 'business' | 'enterprise';
  reason: string;
  expiresAt?: string | null;
}

export const setTenantPlanOverride = (
  tenantId: string,
  input: TenantPlanOverrideInput,
) =>
  post<unknown>(`/tenants/${encodeURIComponent(tenantId)}/plan-override`, input);

export const clearTenantPlanOverride = (tenantId: string) =>
  request<unknown>(
    "DELETE",
    `/tenants/${encodeURIComponent(tenantId)}/plan-override`,
  );

// ── Tenant 360° detail ───────────────────────────────────────────────
export interface TenantDetail {
  id: string;
  name: string;
  slug: string;
  plan: string;
  status: 'active' | 'trial' | 'suspended' | 'cancelled';
  ownerEmail: string;
  ownerName: string;
  userCount: number;
  businessAddress: string;
  activitySector: string;
  businessSize: string;
  createdAt: string;
  updatedAt: string;
  suspendedAt: string | null;
  suspendedReason: string;
  terminatedAt: string | null;
  terminatedReason: string;
  terminatedGraceUntil: string | null;
  lastActivityAt: string | null;
  health: 'healthy' | 'attention' | 'critical';
  // Slice D — plan override metadata. planOverride is present when an
  // admin has manually overridden the effective plan; rawPlan exposes
  // the underlying tenants.plan so the UI can show both. When override
  // is null/missing, plan === rawPlan and rawPlan may be omitted.
  rawPlan?: string;
  planOverride?: {
    plan: string;
    reason: string;
    setAt: string | null;
    expiresAt: string | null;
    setByAdminId: string | null;
  } | null;
}

export interface TenantUserRow {
  id: string;
  email: string;
  fullName: string;
  role: string;
  active: boolean;
  mfaEnabled: boolean;
  lastLoginAt: string | null;
}

export interface TenantAuditRow {
  id: string;
  actor: string;
  action: string;
  resourceType: string | null;
  resourceId: string | null;
  result: 'success' | 'failure' | 'denied';
  occurredAt: string;
  payload: Record<string, unknown> | null;
}

export interface TenantBillingActionRow {
  id: string;
  tenantId: string;
  amountCents: number;
  reason: string;
  actorId: string;
  actorEmail: string;
  status: 'requested' | 'completed' | 'failed';
  stripeRefundId?: string;
  stripeChargeId?: string;
  stripeBalanceTransactionId?: string;
  errorMessage?: string;
  createdAt: string;
  updatedAt: string;
}

export const getTenantDetail = (tenantId: string) =>
  get<TenantDetail>(`/tenants/${encodeURIComponent(tenantId)}`);

export const listTenantUsers = (tenantId: string) =>
  get<TenantUserRow[]>(`/tenants/${encodeURIComponent(tenantId)}/users`);

export const listTenantAudit = (tenantId: string) =>
  get<TenantAuditRow[]>(`/tenants/${encodeURIComponent(tenantId)}/audit`);

export const getTenantBillingActions = (tenantId: string) =>
  get<{ refunds: TenantBillingActionRow[]; credits: TenantBillingActionRow[] }>(
    `/tenants/${encodeURIComponent(tenantId)}/billing-actions`,
  );

// ── Slice F — role-scoped dashboards ─────────────────────────────────
// Five new endpoint groups under /admin-bff/{legal,tech-support,
// live-agent,billing-admin,technical}, each gated by a `dashboard:*`
// permission seeded in admin-bff migration 0006. These types must
// stay in sync with the backend controllers (admin-bff PR #8).

export type LegalMatterStatus =
  | "open"
  | "awaiting_counsel"
  | "awaiting_signature"
  | "awaiting_sign_off"
  | "filed"
  | "escalated"
  | "privileged_review"
  | "classified"
  | "closed";

export type LegalMatterUrgency = "classified_72hr" | "standard" | "low";

export type LegalMatterType =
  | "gdpr_erasure"
  | "dpa_review"
  | "scc_refresh"
  | "impersonation_review"
  | "retention_sweep"
  | "restrictive_covenant"
  | "data_subject_access"
  | "breach_incident"
  | "other";

export interface LegalMatter {
  id: string;
  caseId: string;
  type: LegalMatterType;
  tenantId: string | null;
  subject: string;
  body: string | null;
  status: LegalMatterStatus;
  urgency: LegalMatterUrgency;
  openedAt: string;
  deadlineAt: string | null;
  closedAt: string | null;
  assignedCounsel: string | null;
  routedBy: string | null;
  particulars: {
    principals?: string[];
    lawful_basis?: string;
    retention_test?: string;
  } | null;
  counselNotes: string | null;
  ageMs: number;
}

export interface LegalSummary {
  activeMatters: number;
  awaitingCounsel: number;
  escalated: number;
  gdprDeletionRequests: number;
  gdprInside72HrClock: number;
  dpaQueue: number;
  dpaMedianAgeDays: number | null;
  dpaOldestAgeDays: number | null;
  compliancePosture: { tier: "A" | "B" | "C"; note: string };
}

export interface LegalAuditEntry {
  id: string | number;
  occurredAt: string;
  actorEmail: string | null;
  action: string;
  resourceType: string | null;
  resourceId: string | null;
  tenantId: string | null;
  payload: Record<string, unknown> | null;
  result: "success" | "failure" | "denied";
}

export const getLegalSummary = () => get<LegalSummary>("/legal/summary");
export const listLegalMatters = (filter?: {
  status?: LegalMatterStatus;
  urgency?: LegalMatterUrgency;
  type?: LegalMatterType;
  limit?: number;
}) => {
  const parts: string[] = [];
  if (filter?.status) parts.push(`status=${encodeURIComponent(filter.status)}`);
  if (filter?.urgency) parts.push(`urgency=${encodeURIComponent(filter.urgency)}`);
  if (filter?.type) parts.push(`type=${encodeURIComponent(filter.type)}`);
  if (filter?.limit != null) parts.push(`limit=${filter.limit}`);
  const qs = parts.length > 0 ? `?${parts.join("&")}` : "";
  return get<LegalMatter[]>(`/legal/matters${qs}`);
};
export const getLegalMatter = (id: string) =>
  get<LegalMatter>(`/legal/matters/${encodeURIComponent(id)}`);
export const updateLegalMatter = (
  id: string,
  patchInput: { status?: LegalMatterStatus; counselNotes?: string },
) =>
  request<LegalMatter>(
    "PATCH",
    `/legal/matters/${encodeURIComponent(id)}`,
    patchInput,
  );
export const listLegalAudit = (days: number = 7, limit: number = 50) =>
  get<{ days: number; entries: LegalAuditEntry[] }>(
    `/legal/audit?days=${days}&limit=${limit}`,
  );

export interface TechSupportQueue {
  totalOpen: number;
  byStatus: Record<string, number>;
  byPriority: { high: number; medium: number; low: number };
  oldestOpenAgeMinutes: number | null;
  unassignedCount: number;
}
export interface TechSupportSla {
  breachedHigh: number;
  breachedMedium: number;
  breachedLow: number;
  breachedTotal: number;
  oldestBreachAgeMinutes: number | null;
  thresholdsMinutes: { high: number; medium: number; low: number };
}
export const getTechSupportQueue = () =>
  get<TechSupportQueue>("/tech-support/queue");
export const getTechSupportSla = () =>
  get<TechSupportSla>("/tech-support/sla");

export interface LiveSession {
  ticketId: string;
  tenantId: string;
  customerName: string;
  customerEmail: string;
  subject: string;
  assignedTo: string | null;
  unreadCount: number;
  ageMinutes: number;
  status: string;
}
export interface SessionsSnapshot {
  active: LiveSession[];
  totalActive: number;
  unassigned: number;
  avgAgeMinutes: number | null;
}
export interface AgentSeat {
  agentId: string;
  activeSessions: number;
  unreadAcrossSessions: number;
  longestAgeMinutes: number;
}
export interface SeatsSnapshot {
  online: AgentSeat[];
  totalActive: number;
  unassignedConversations: number;
}
export const getLiveAgentSessions = () =>
  get<SessionsSnapshot>("/live-agent/sessions");
export const getLiveAgentSeats = () => get<SeatsSnapshot>("/live-agent/seats");

export interface BillingAdminOverview {
  mrrCents: number;
  failedPayments: number;
  trialsActive: number;
  trialsAvgDaysLeft: number | null;
  churnRiskAccounts: number;
}
export interface BillingPlanOverride {
  tenantId: string;
  tenantName: string;
  plan: string;
  status: string;
  appliedAt: string | null;
  appliedBy: string | null;
  reason: string | null;
}
export const getBillingAdminOverview = () =>
  get<BillingAdminOverview>("/billing-admin/overview");
export const listBillingPlanOverrides = () =>
  get<BillingPlanOverride[]>("/billing-admin/plan-overrides");

// Pillar 5 — coupons. Stripe is the source of truth; admin-bff proxies to the
// billing service's /admin/coupons endpoints. Gated by `billing:coupons`.
export type CouponDuration = "once" | "repeating" | "forever";
export interface Coupon {
  promotionCodeId: string;
  code: string;
  couponId: string;
  percentOff?: number;
  amountOffCents?: number;
  currency?: string;
  duration: CouponDuration;
  durationMonths?: number;
  maxRedemptions?: number;
  timesRedeemed: number;
  expiresAtUnix?: number;
  active: boolean;
  eligiblePlans?: string[];
  campaignName?: string;
}
export interface CreateCouponInput {
  code: string;
  percentOff?: number;
  amountOffCents?: number;
  currency?: string;
  duration: CouponDuration;
  durationMonths?: number;
  maxRedemptions?: number;
  expiresAtUnix?: number;
  eligiblePlans?: string[];
  campaignName?: string;
}
export const listCoupons = () => get<Coupon[]>("/billing-admin/coupons");
export const createCoupon = (input: CreateCouponInput) =>
  post<Coupon>("/billing-admin/coupons", input);
export const deactivateCoupon = (promotionCodeId: string) =>
  del<{ success: true }>(
    `/billing-admin/coupons/${encodeURIComponent(promotionCodeId)}`,
  );

export interface TechnicalServiceHealth {
  name: string;
  url: string;
  status: "healthy" | "degraded" | "unreachable";
  latencyMs: number | null;
  detail: string | null;
  checkedAt: string;
}
export interface TechnicalAlert {
  id: string | number;
  occurredAt: string;
  action: string;
  resourceType: string | null;
  resourceId: string | null;
  payload: Record<string, unknown> | null;
  result: "success" | "failure" | "denied";
}
export const getTechnicalInfraHealth = () =>
  get<{ services: TechnicalServiceHealth[] }>("/technical/infra-health");
export const getTechnicalAlerts = () =>
  get<{ recent: TechnicalAlert[]; sinceUtc: string }>("/technical/alerts");

/**
 * Kick off Google Workspace SSO. We do a full-page navigation (not fetch)
 * so the browser follows admin-bff's 302 to Google and back. After the
 * round-trip, admin-bff sets the admin_session cookie and redirects to
 * ADMIN_PORTAL_URL (this site), where AuthGate re-fetches /auth/me.
 */
export const startGoogleLogin = (): void => {
  if (typeof window !== "undefined") {
    window.location.href = `${API_URL}/auth/google`;
  }
};

// ── Domain-bound sign-in link ─────────────────────────────────────────
// Provider-independent sign-in (2026-09-30): a one-time link is emailed to an
// admin address on the company domain, whoever hosts that domain's mail. The
// request answers 202 whether or not the address is known. The link opens
// this SPA at #signin-link=<token>; the SPA redeems it with a POST (mail
// providers prefetch links, so the GET itself must never sign in). The BFF
// then sets admin_session, or admin_mfa_pending when MFA is enrolled.
export const requestSigninLink = (email: string) =>
  post<{ ok: true }>("/auth/signin-link/request", { email });
export const redeemSigninLink = (token: string) =>
  post<{ next: "mfa" | "portal" }>("/auth/signin-link/redeem", { token });

// ----- Pillar 4: marketplace app-review desk -----

export interface ReviewQueueItem {
  versionId: string; appId: string; slug: string; name: string; version: string;
  status: string; priority: "high" | "standard" | "low"; certificationPassed: boolean;
  submittedAt: string; submittedByEmail: string | null;
  assignedReviewerId: string | null; assignedReviewerEmail: string | null; assignedAt: string | null;
  ageHours: number; slaHours: number; slaBreached: boolean;
}
export interface SlaThresholds { high: number; standard: number; low: number; }
export interface ReviewQueueResponse { slaConfig: SlaThresholds; queue: ReviewQueueItem[]; }
export interface ReviewDetail {
  versionId: string; name?: string; slug?: string; version: string; status: string;
  priority: string; certificationPassed: boolean; certificationReport: unknown; manifest: unknown;
  reviewPacket: unknown; reviewNotes: string | null; submittedAt: string; submittedByEmail: string | null;
  assignedReviewerEmail: string | null;
}
export interface PublishedApp {
  id: string; slug: string; name: string; category: string; publisher: string;
  installCount: number; currentVersion: string | null; status: string;
}

export const getReviewQueue = () => get<ReviewQueueResponse>("/marketplace-admin/review/queue");
export const getReviewDetail = (id: string) =>
  get<ReviewDetail>(`/marketplace-admin/review/${encodeURIComponent(id)}`);
export const assignReview = (id: string, body: { reviewerId?: string; reviewerEmail?: string }) =>
  post(`/marketplace-admin/review/${encodeURIComponent(id)}/assign`, body);
export const setReviewPriority = (id: string, priority: string) =>
  post(`/marketplace-admin/review/${encodeURIComponent(id)}/priority`, { priority });
export const publishReview = (id: string, notes?: string) =>
  post(`/marketplace-admin/review/${encodeURIComponent(id)}/publish`, { notes });
export const rejectReview = (id: string, notes: string) =>
  post(`/marketplace-admin/review/${encodeURIComponent(id)}/reject`, { notes });
export const getReviewSla = () => get<SlaThresholds>("/marketplace-admin/review/sla-config");
export const setReviewSla = (body: SlaThresholds) =>
  request<SlaThresholds>("PUT", "/marketplace-admin/review/sla-config", body);
export const getPublishedApps = () =>
  get<{ apps: PublishedApp[] }>("/marketplace-admin/review/published");
export const delistApp = (appId: string) =>
  post(`/marketplace-admin/review/apps/${encodeURIComponent(appId)}/delist`);
export const deleteMarketplaceApp = (appId: string) =>
  del(`/marketplace-admin/review/apps/${encodeURIComponent(appId)}`);

// ----- Pillar 6A: Employee OS (People) -----

export type Lifecycle = "onboarding" | "active" | "offboarding" | "departed";
export interface EmployeeRow {
  id: string; email: string; fullName: string; title: string | null;
  department: string | null; managerName: string | null; roleKeys: string[];
  employmentType: string | null; workLocation: string | null; startDate: string | null;
  lifecycleStatus: Lifecycle; active: boolean;
}
export interface EmployeeChecklistItem { id: string; title: string; description: string | null; assigneeId: string | null; status: "pending" | "done"; sortOrder: number; }
export interface EmployeeChecklist { id: string; kind: "onboarding" | "offboarding"; status: "in_progress" | "complete"; createdAt: string; completedAt: string | null; items: EmployeeChecklistItem[]; }
export interface EmployeeProfile extends EmployeeRow {
  phone: string | null; bio: string | null;
  emergencyContact: { name?: string; phone?: string; relationship?: string } | null;
  checklists: EmployeeChecklist[];
}
export interface OnboardingTemplate {
  id: string; name: string; kind: "onboarding" | "offboarding"; departmentId: string | null; active: boolean;
  items: Array<{ id: string; title: string; description: string | null; sortOrder: number }>;
}

export const getEmployees = (q?: { status?: string; departmentId?: string }) => {
  const p = new URLSearchParams();
  if (q?.status) p.set("status", q.status);
  if (q?.departmentId) p.set("departmentId", q.departmentId);
  const qs = p.toString();
  return get<EmployeeRow[]>(`/people/employees${qs ? `?${qs}` : ""}`);
};
export const getEmployeeProfile = (id: string) =>
  get<EmployeeProfile>(`/people/employees/${encodeURIComponent(id)}`);
export const updateEmployeeProfile = (id: string, body: Record<string, unknown>) =>
  request<EmployeeProfile>("PATCH", `/people/employees/${encodeURIComponent(id)}`, body);
export const getOnboardingTemplates = () => get<OnboardingTemplate[]>("/people/templates");
export const createOnboardingTemplate = (body: { name: string; kind: string; departmentId?: string | null; items: Array<{ title: string; description?: string }> }) =>
  post("/people/templates", body);
export const deleteOnboardingTemplate = (id: string) =>
  del(`/people/templates/${encodeURIComponent(id)}`);
export const startEmployeeChecklist = (id: string, templateId: string) =>
  post<EmployeeProfile>(`/people/employees/${encodeURIComponent(id)}/checklists`, { templateId });
export const toggleChecklistItem = (checklistId: string, itemId: string, done: boolean) =>
  request<EmployeeProfile>("PATCH", `/people/checklists/${encodeURIComponent(checklistId)}/items/${encodeURIComponent(itemId)}`, { done });

export { ApiError };

// ─────────────────────────────────────────────────────────────────────────────
// Guardrail approvals — CRITICAL destructive actions awaiting human confirmation.
//
// Distinct from the Approvals queue above, which is the internal business
// workflow (refunds, suspensions). These are platform GUARDRAIL decisions: a
// tenant purge cannot execute until two distinct admins each type a challenge
// naming the tenant and re-prove their second factor.
// ─────────────────────────────────────────────────────────────────────────────

export type GuardrailStatus = "PENDING" | "READY" | "CONSUMED" | "REJECTED" | "EXPIRED";

export interface GuardrailPendingAction {
  id: string;
  tenant_id: string;
  action: string;
  object_type: string;
  object_id?: string;
  risk_level: string;
  status: GuardrailStatus;
  pathway: string;
  approval_count: number;
  required_approvals: number;
  expires_at: string;
  requested_by_type: string;
  requested_by_id?: string;
  requested_by_human?: string;
  action_digest: string;
  confirmation_kind?: string;
  /** Displayed to the approver — the point is that they have to READ which
   *  tenant they are destroying, not that they have to guess a password. */
  confirmation_phrase?: string;
  execution_id?: string;
  execution_expires_at?: string;
}

export const listGuardrailPending = (status?: GuardrailStatus) =>
  get<{ pending_actions: GuardrailPendingAction[] }>(
    `/policy/pending${status ? `?status=${encodeURIComponent(status)}` : ""}`,
  ).then((r) => r.pending_actions ?? []);

export const getGuardrailPending = (id: string) =>
  get<GuardrailPendingAction>(`/policy/pending/${encodeURIComponent(id)}`);

/**
 * Records ONE admin's approval. Both the typed challenge and a fresh
 * second-factor code are required: the server verifies the code before signing
 * anything, and re-checks the phrase against the value it stored, so neither is
 * taken on the client's word.
 */
export const approveGuardrailPending = (id: string, confirmationText: string, mfaCode: string) =>
  post<{ status: GuardrailStatus; approval_count: number; required_approvals: number }>(
    `/policy/pending/${encodeURIComponent(id)}/approve`,
    { confirmationText, mfaCode },
  );

export const revokeGuardrailPending = (id: string, reason: string) =>
  post<GuardrailPendingAction>(`/policy/pending/${encodeURIComponent(id)}/revoke`, { reason });

// ─────────────────────────────────────────────────────────────────────────────
// Platform (AEOS) approvals — governed Tool Gateway changes awaiting one admin.
//
// Registering a tool, adapter or risk rule on the Tool Gateway is authorized by
// the policy engine in the scope `platform:tool-gateway` and cannot complete
// until an Ivorycom admin types the operation and re-proves their second
// factor. The engine verifies the signed assertion and the approver's role.
// ─────────────────────────────────────────────────────────────────────────────

export type PlatformApprovalState = "PENDING" | "GRANTED" | "REJECTED" | "WITHDRAWN" | "EXPIRED" | "CONSUMED";

export interface PlatformApproval {
  approval_id: string;
  tenant_id: string;
  state: PlatformApprovalState | string;
  decision_id: string;
  bound_intent_hash: string;
  binding_digest: string;
  tool_id: string;
  operation: string;
  materiality: string;
  requested_by_actor: string;
  requested_by_actor_type: string;
  requested_by_workload?: string;
  requested_at: string;
  required_roles: string[];
  approver_subject?: string;
  decided_at?: string;
  decision_reason?: string;
  expires_at: string;
}

/** The phrase an admin types: the operation being authorized. */
export const platformApprovalPhrase = (a: Pick<PlatformApproval, "tool_id" | "operation">) => `${a.tool_id}/${a.operation}`;

export const listPlatformApprovals = (state?: PlatformApprovalState) =>
  get<{ tenant_id: string; approvals: PlatformApproval[] }>(
    `/policy/platform-approvals${state ? `?state=${encodeURIComponent(state)}` : ""}`,
  ).then((r) => r.approvals ?? []);

export const approvePlatformApproval = (id: string, confirmationText: string, mfaCode: string) =>
  post<PlatformApproval>(`/policy/platform-approvals/${encodeURIComponent(id)}/approve`, { confirmationText, mfaCode });

export const rejectPlatformApproval = (id: string, reason: string) =>
  post<PlatformApproval>(`/policy/platform-approvals/${encodeURIComponent(id)}/reject`, { reason });
